import { useId, useState, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { foundation } from '../../ui';
import { semantic } from '../../ui/theme';
import { alphaOf } from '../../utils/ui/colorAlpha';
import { fmtMoneyInput, clampMoneyInput, stripCommas, formatCurrency } from '../../utils/format/format';
import { MONEY_SOFT_WARNING_DEFAULT } from '../../utils/validation/moneyWarning';
import { exactFieldProps } from '../../utils/ui/textEntryProps';
import { MONEY_LIMITS, comparePriceToUsual } from '../../utils/money/moneyLimits';
import { priceVsUsualText } from '../../utils/money/priceVsUsualText';
import { useResponsiveLayout } from '../../hooks/ui/useResponsiveLayout';
import MoneyInputNote, { prefersReducedMotion } from './MoneyInputNote';

/**
 * At-rest display: whole money, always two decimals ("1000" → "1,000.00").
 * Blank stays blank so the placeholder shows rather than a misleading "0.00",
 * and un-parseable junk falls through to the raw formatter untouched.
 */
export function restingMoneyDisplay(value) {
  const stripped = stripCommas(String(value ?? '').trim());
  if (!stripped) return '';
  const num = parseFloat(stripped);
  if (!Number.isFinite(num)) return fmtMoneyInput(value ?? '');
  return fmtMoneyInput(num.toFixed(2));
}

/**
 * iOS zooms the page when an input under 16px takes focus — but that behaviour
 * exists only on TOUCH. On a mouse there is no auto-zoom, and a hardcoded 16px
 * left the money field visibly larger than every sibling field in the same form
 * (they all render at foundation.fontSize.sm). So size by POINTER TYPE: keep the
 * 16px guarantee where it does something, match the rest of the app where it
 * doesn't.
 *
 * Exported pure so the rule is testable without a DOM.
 */
export function moneyFieldFontSize(coarsePointer, compact = false) {
  if (!coarsePointer) return foundation.fontSize.sm;
  return compact ? '14px' : '16px';
}

// Pointer type does not change mid-session, so resolve once. Unknown (SSR /
// jsdom, where matchMedia may be absent) is treated as COARSE — that errs
// toward the no-zoom guarantee rather than risking a zoom on a real phone.
let _coarsePointer = null;
function isCoarsePointer() {
  if (_coarsePointer === null) {
    _coarsePointer = typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? true
      : window.matchMedia('(pointer: coarse)').matches;
  }
  return _coarsePointer;
}

const stopBubble = (ev) => ev.stopPropagation();

// Default above-typical soft-warning threshold. MOVED to
// `src/utils/validation/moneyWarning.js` (2026-09-16) so pure utils
// (`extraPriceHintLine.js`) can import the constant without pulling
// `react-intl` into Jest via this component — re-exported here so every
// existing `import { MONEY_SOFT_WARNING_DEFAULT } from '.../MoneyInput'`
// stays untouched. Exported so a parent that owns the warning copy itself
// (e.g. LineItemRow's labeled extra layout, which renders the helper
// full-width below the row instead of under the field) can use the SAME
// cutoff — keeps the field tint and the parent's helper text in lockstep.
export { MONEY_SOFT_WARNING_DEFAULT };

/**
 * Money input with $ prefix, right-aligned digits, and 2-decimal padding on
 * blur. Wraps the existing fmtMoneyInput / clampMoneyInput / stripCommas
 * helpers — display + formatting concerns live here, parent stores raw string.
 *
 * `intent='admin'` switches to the amber border tone used for the JobDetail
 * price an admin has tapped to edit, signalling a privileged edit.
 *
 * Caller still owns onChange — receives the cleaned, comma-stripped, decimal-
 * clamped string. On blur the same string is re-emitted padded to two
 * decimals (e.g. "125" → "125.00") so totals stay consistent and inputs
 * read like real money values.
 *
 * `forceWarning` lets a parent that computes a richer warning rule of its
 * own (e.g. history-relative, per `getExtraPriceWarning`) drive the amber
 * field tint without this component re-deriving it from `softWarningThreshold`
 * — the parent still owns the helper copy itself via the existing
 * `suppressWarningText`. It only ever ADDS to the built-in above-threshold
 * check (`showSoftWarning || forceWarning`), so an omitted/false value is a
 * no-op and every existing caller renders byte-identically.
 *
 * The 2026-09-16 Office Inbox "Ledger" redesign added a second batch of
 * opt-in props (`focusRing`, `filled`, `statusTone`, `radius`, `readOnly`,
 * `onEnter`, `enterKeyHint`, `inputProps`) for the compact, one-field-one-
 * price ledger row (`PriceExtraLineRow`). Every one of them is OFF by
 * default and changes nothing for the ~20 existing callers that don't pass
 * them — see `MoneyInput.test.js` for the byte-identical-render guard.
 */
export default function MoneyInput({
  value,
  onChange,
  disabled = false,
  placeholder = '0.00',
  intent = 'default',         // 'default' | 'admin' | 'ready' (navy ring — a non-blank value in a "one row, one price" layout, e.g. inline PriceExtraLineRow)
  ariaLabel,
  maxValue = MONEY_LIMITS.linePrice,
  softWarningThreshold = MONEY_SOFT_WARNING_DEFAULT,
  compact = false,            // shrinks padding + font for tighter modal forms
  suppressWarningText = false, // keep the amber field tint, but let the parent render the helper copy
  minHeight,                  // px override — omit to keep the default/compact height
  tabularNums = false,        // font-variant-numeric: tabular-nums, for fixed-width money columns
  forceWarning = false,       // parent-computed warning (e.g. history-relative) also tints the field amber
  focusRing = true,           // canon (KAN-856): ALWAYS on while focused — prop kept for compatibility; pass false only to suppress
  filled = false,             // a valued field at rest: neutral.borderStrong border
  statusTone = null,          // 'success' | 'danger' | null — wins over focusRing/warning
  radius,                     // px override for the wrap's border-radius (spread last)
  readOnly = false,           // native readOnly — NOT disabled (iOS washes out disabled text)
  onEnter,                    // (event) => void — when set, Enter calls this INSTEAD of the default blur
  enterKeyHint,                // passed straight through to the <input>
  inputProps,                  // spread onto <input> for data-*; never overrides the controlled props below
  usualPrice,                  // the item's usual price (catalog / last charged): warns far above / below it (KAN-1061)
  externalMaxNote = false,     // KAN-1066: the row shows the "Maximum" line itself (web extra rows' message line) - this box renders only the amber warning
  maxNoteOnRefuse = false,     // KAN-1085: show "Maximum" only after a refused keystroke, not merely because the value sits at the cap (windows prefilled at the cap)
  onMaxNoteChange,             // (text|null) => void - reports the Maximum line's text while externalMaxNote is set
  noteInset,                   // KAN-1084: CSS length - the note may run this far past the box's right edge (the kit's one-row price column): one line in practice, wraps only past the row's edge
}) {
  const id = useId();
  const intl = useIntl();
  const { isTouch } = useResponsiveLayout();
  const isAdmin = intent === 'admin';
  const isReady = intent === 'ready';

  // Two-decimal padding happens on BLUR (handleBlur), which only ever fixes
  // fields the user actually touched. A price loaded from the DB and never
  // focused stayed unpadded — Postgres `numeric(10,2)` arrives over PostgREST
  // as the JSON number 1000, so String(1000) → "1000" → "1,000" on screen.
  //
  // So pad the DISPLAY while the field is at rest, and show the raw value while
  // it has focus. The focus split is load-bearing: padding a focused field
  // would rewrite "5" to "5.00" mid-keystroke and make "5.5" impossible to type.
  //
  // Display-only, deliberately — this never calls onChange, so opening a drawer
  // cannot mark a pristine form dirty or enable a disabled Save button.
  const [focused, setFocused] = useState(false);
  const fieldFontSize = moneyFieldFontSize(isCoarsePointer(), compact);
  const display = focused ? fmtMoneyInput(value ?? '') : restingMoneyDisplay(value);

  // Shake-on-rejection: when a keystroke would push the value past
  // maxValue, we refuse it and bump this counter. The useEffect below
  // restarts the cvx-input-shake animation by clearing then re-applying
  // the animation property on the wrapper (standard CSS-restart pattern).
  const wrapRef = useRef(null);
  const [shakeNonce, setShakeNonce] = useState(0);
  // KAN-1061: a refused over-cap keystroke also raises the "Maximum $…" line,
  // kept while the value sits AT the cap or until the next valid keystroke.
  const [capHit, setCapHit] = useState(false);
  // KAN-1085: with maxNoteOnRefuse, a new cap (bucket switch) starts clean - no
  // "Maximum" carried over from a refusal against the previous cap.
  useEffect(() => {
    if (maxNoteOnRefuse) setCapHit(false);
  }, [maxValue, maxNoteOnRefuse]);
  useEffect(() => {
    if (shakeNonce === 0) return;
    const el = wrapRef.current;
    if (!el || prefersReducedMotion()) return;
    el.style.animation = 'none';
    // Force reflow so the next animation assignment is treated as a fresh start.
    void el.offsetHeight;
    el.style.animation = 'cvx-input-shake 320ms ease-in-out';
  }, [shakeNonce]);

  const handleChange = (e) => {
    // Sanitize: keep only digits and at most one decimal point. Any other
    // character (dashes, em-dashes, letters, currency symbols, leading +/-)
    // is silently dropped. Without this guard, parseFloat('—') returns NaN
    // and the raw string propagates to state — downstream Number() then
    // renders "NaN" in the display, which is what the founder observed in
    // admin price-edit mode.
    const dirty = stripCommas(e.target.value);
    let sanitized = '';
    let sawDot = false;
    for (const ch of dirty) {
      if (ch >= '0' && ch <= '9') sanitized += ch;
      else if (ch === '.' && !sawDot) { sanitized += ch; sawDot = true; }
      // anything else: skipped
    }
    // Reject keystrokes that would push the value above maxValue (HTML
    // maxlength semantics, not "rewrite to max"). Negatives are now
    // structurally impossible after sanitization, so the < 0 guard is
    // belt-and-suspenders. Bumping shakeNonce restarts the wrapper's
    // shake animation so the rejection has visible feedback.
    const rawNumeric = parseFloat(sanitized);
    if (Number.isFinite(rawNumeric) && (rawNumeric > maxValue || rawNumeric < 0)) {
      setShakeNonce((n) => n + 1);
      if (rawNumeric > maxValue) setCapHit(true);
      return;
    }
    setCapHit(false);
    const cleaned = clampMoneyInput(sanitized, maxValue, 2);
    onChange?.(cleaned);
  };

  const numericValue = parseFloat(stripCommas(String(value ?? '')));
  const priceCheck = comparePriceToUsual(value, usualPrice, { flat: softWarningThreshold });
  const showSoftWarning = Boolean(priceCheck) && Number.isFinite(numericValue);
  const atCap = Number.isFinite(numericValue) && Number.isFinite(maxValue) && numericValue === maxValue;

  const handleBlur = () => {
    const stripped = stripCommas(String(value ?? '').trim());
    if (!stripped) return;
    const num = parseFloat(stripped);
    // Junk state self-heal: if the current value isn't a finite number
    // (e.g. "NaN--------" left over before the sanitizer landed, or stale
    // garbage from a paste), reset to empty so the display stops showing
    // gibberish.
    if (!Number.isFinite(num)) { onChange?.(''); return; }
    const padded = num.toFixed(2);
    if (padded !== stripped) onChange?.(padded);
  };

  // Root is block-level flex (not inline-flex) so the control fills its parent
  // column — form fields want full width, which the `flex: 1` here states.
  // Inside a flex ROW the item is blockified anyway, so row usages are
  // unchanged; only block-parent form fields go from shrink-to-content → fill.
  const warnActive = (showSoftWarning || forceWarning) && !disabled;
  const warnText = warnActive && !suppressWarningText ? priceVsUsualText(intl, priceCheck) : null;
  // The max line is info, never a warning: it does not tint the border and
  // yields to the amber line (a value at the cap is under the flat amount rarely).
  const showMax = (capHit || (atCap && !maxNoteOnRefuse)) && !disabled && Number.isFinite(maxValue);
  const noteId = `${id}-note`;
  const maxText = showMax
    ? intl.formatMessage({ id: 'money.maxNote', defaultMessage: 'Maximum {max}' }, { max: formatCurrency(maxValue) })
    : null;
  // External mode: the row owns the Maximum line, so this box keeps only the amber warning.
  const noteText = externalMaxNote ? warnText : (maxText || warnText);
  const reportedMax = externalMaxNote ? maxText : null;
  useEffect(() => {
    if (externalMaxNote) onMaxNoteChange?.(reportedMax);
  }, [externalMaxNote, reportedMax]); // eslint-disable-line react-hooks/exhaustive-deps

  // RD-UI-036: always set borderColor + boxShadow explicitly. Spreading a
  // focus longhand (borderColor/boxShadow) over a border shorthand, then
  // omitting those keys on blur, left a stuck dark rim on WebKit/Chromium
  // after click-outside / keyboard dismiss (Precios pendientes, Extras, …).
  const wrapChrome = resolveWrapChrome({
    isAdmin, isReady, filled, focusRing: focusRing && focused, warnActive, statusTone,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
      <div
        ref={wrapRef}
        style={{
          ...wrapStyle,
          minHeight: isTouch ? '44px' : wrapStyle.minHeight,   // F1: same height as the text fields around it
          ...(compact ? wrapCompactStyle : {}),
          borderColor: wrapChrome.borderColor,
          boxShadow: wrapChrome.boxShadow,
          background: wrapChrome.background ?? wrapStyle.background,
          ...(minHeight != null ? { minHeight: `${minHeight}px` } : {}),
          ...(radius != null ? { borderRadius: `${radius}px` } : {}),
        }}
        aria-disabled={disabled}
        data-testid="money-input-wrap"
        data-cvx-money-wrap=""
      >
        <span
          style={{
            ...prefixStyle,
            ...(isAdmin ? prefixAdminStyle : {}),
            ...(compact ? prefixCompactStyle : {}),
            fontSize: fieldFontSize,   // last: must match the input beside it
          }}
          aria-hidden
          data-cvx-field-adornment=""
        >
          $
        </span>
        <input
          {...exactFieldProps}
          id={id}
          type="text"
          inputMode="decimal"
          data-cvx-money-input=""
          {...inputProps}
          value={display}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          enterKeyHint={enterKeyHint}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); handleBlur(); }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            if (onEnter) { e.preventDefault(); onEnter(e); return; }
            e.currentTarget.blur();
          }}
          onTouchStart={stopBubble}
          onMouseDown={stopBubble}
          style={{
            ...inputStyle,
            ...(compact ? inputCompactStyle : {}),
            fontSize: fieldFontSize,
            ...(tabularNums ? { fontVariantNumeric: 'tabular-nums' } : {}),
            ...(statusTone === 'success' ? { color: foundation.color.neutral.textSecondary } : {}),
          }}
          aria-label={ariaLabel}
          aria-describedby={noteText ? [inputProps?.['aria-describedby'], noteId].filter(Boolean).join(' ') : inputProps?.['aria-describedby']}
          // `statusTone="danger"` is the ONLY invalid signal this input has —
          // its border lives on the wrap div, not this element, so a
          // completeness-gate consumer marking a blank/invalid price danger
          // needs aria-invalid set here too (screen readers never see the div).
          aria-invalid={statusTone === 'danger' ? 'true' : undefined}
        />
      </div>
      {noteInset ? (
        <div style={{ width: `calc(100% + ${noteInset})`, maxWidth: `calc(100% + ${noteInset})` }} data-testid="money-input-note-extend">
          <MoneyInputNote id={noteId} tone={showMax && !externalMaxNote ? 'max' : 'warn'} text={noteText || ''} show={Boolean(noteText)} />
        </div>
      ) : (
        <MoneyInputNote id={noteId} tone={showMax && !externalMaxNote ? 'max' : 'warn'} text={noteText || ''} show={Boolean(noteText)} />
      )}
    </div>
  );
}

// Border is the shared text-field border (`neutral.borderField`) and the height
// the shared field height (40 on web, 44 under a finger), so a money field reads
// as one of the text fields around it.
// Longhands only (RD-UI-036) — never mix `border` shorthand with later
// borderColor overrides that get omitted on blur.
const wrapStyle = {
  display: 'inline-flex', alignItems: 'stretch',
  background: foundation.color.neutral.surface,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: foundation.color.neutral.borderField,
  boxShadow: 'none',
  borderRadius: foundation.radii.md,
  overflow: 'visible',
  minHeight: '40px',
  flex: 1, minWidth: 0,
  fontFamily: foundation.fontFamily.sans,
  transition: 'border-color 150ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 150ms cubic-bezier(0.16, 1, 0.3, 1), background-color 200ms ease',
};

/**
 * Resolve wrap border/ring for the current props. Order matches the old
 * spread stack: admin → ready → filled → focusRing → warning → status.
 * Every call returns both borderColor and boxShadow so blur never leaves
 * a sticky longhand from the previous focused paint (RD-UI-036).
 */
export function resolveWrapChrome({
  isAdmin = false,
  isReady = false,
  filled = false,
  focusRing = false,
  warnActive = false,
  statusTone = null,
} = {}) {
  let borderColor = foundation.color.neutral.borderField;
  let boxShadow = 'none';
  let background;
  if (isAdmin) {
    borderColor = alphaOf(semantic.legacy.warning, 0.55);
    boxShadow = `0 0 0 2px ${alphaOf(semantic.legacy.warning, 0.1)}`;
  }
  if (isReady) {
    borderColor = foundation.color.brand.primary;
    boxShadow = `0 0 0 2px ${foundation.color.brand.primaryMuted}`;
  }
  // filled only tightens the border; keeps any admin/ready ring underneath
  if (filled) borderColor = foundation.color.neutral.borderStrong;
  if (focusRing) {
    borderColor = foundation.color.brand.primary;
    boxShadow = foundation.shadow.fieldFocus;
  }
  if (warnActive) {
    borderColor = foundation.color.semantic.warning;
    boxShadow = `0 0 0 3px ${foundation.color.semantic.warningSoft}`;
  }
  if (statusTone === 'success') {
    background = foundation.color.neutral.surfaceMuted;
    borderColor = foundation.color.neutral.surfaceMuted;
    boxShadow = 'none';
  } else if (statusTone === 'danger') {
    borderColor = foundation.color.semantic.danger;
    boxShadow = foundation.shadow.fieldFocusDanger;
  }
  return { borderColor, boxShadow, background };
}

// Flat affix: no filled box / divider — just a muted `$` glyph inside the
// field, so the control reads lighter and matches the plain inputs elsewhere.
const prefixStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  paddingLeft: '10px', paddingRight: '2px',
  background: 'transparent',
  color: foundation.color.neutral.textMuted,
  // Same size + weight as the input beside it. They used to differ (0.95rem/600
  // vs 16px/600), and glyphs at two different sizes cannot share an optical
  // baseline no matter how the flexbox aligns them — the `$` read as floating.
  fontSize: '16px',
  fontWeight: foundation.fontWeight.normal,
  userSelect: 'none',
  flexShrink: 0,
};

const prefixAdminStyle = {
  color: foundation.color.semantic.warningText,
};

const inputStyle = {
  flex: 1, minWidth: '120px',
  // WebKit paints a harsh black rim on text fields after keyboard dismiss unless
  // appearance is fully reset (same family as the tab "thick black rim" fix).
  // Focus chrome lives on the WRAP (focusRing / ready / warning) — never here.
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  border: 'none',
  outline: 'none',
  boxShadow: 'none',
  WebkitTapHighlightColor: 'transparent',
  background: 'transparent',
  padding: '6px 10px 6px 6px',
  // Fallback only — the live value comes from moneyFieldFontSize() at render,
  // which keeps 16px on touch (iOS zoom guard) and drops to the app's 13px on a
  // mouse, where no such guard is needed.
  fontSize: '16px',
  // Normal weight, matching `p.field` and every other input. Semibold here made
  // an editable field read like rendered output.
  fontWeight: foundation.fontWeight.normal,
  fontFamily: foundation.fontFamily.sans,
  color: foundation.color.neutral.text,
  textAlign: 'left',
  touchAction: 'manipulation',
};

// Compact variant: tighter height, smaller font. Use for in-modal forms
// where the canonical 16px input would dominate. Trades the iOS no-zoom
// guarantee — acceptable for desktop-leaning surfaces.
const wrapCompactStyle = {
  minHeight: '32px',
  borderRadius: '6px',
};

const prefixCompactStyle = {
  paddingLeft: '8px', paddingRight: '2px',
  // Fallback only: moneyFieldFontSize() is spread AFTER this at render, so the
  // live value comes from there. Kept so the constant is self-contained.
  fontSize: '14px',
};

const inputCompactStyle = {
  padding: '4px 8px 4px 4px',
  fontSize: '14px',
  minWidth: '90px',
};
